import { Link } from 'react-router-dom';
import { Container, Section } from '@/components/layout';
import { GoldLine, SacredGeometry, Sparkle } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { IconFeature, TicketCta } from '@/components/ui';
import { Prose } from '@/components/typography';
import { formatDateRangeLong, type TourEvent } from '@/features/events';
import { AssetImage } from '@/pages/shared/AssetImage';
import { BA_AZRAHEL, BA_CHAMBER, BA_TICKETS } from '../ba.content';
import styles from './BaBlocks.module.css';

interface Props {
  event: TourEvent;
}

/** 3 · Fila de 4 beneficios con filete vertical entre columnas. */
export function BaBenefits({ event }: Props) {
  if (event.benefits.length === 0) return null;

  return (
    <Section background="alt" spacing="tight" width="default" className={styles.benefitsBand}>
      <ul className={styles.benefits} role="list">
        {event.benefits.map((benefit, index) => (
          <Reveal as="li" className={styles.benefit} delay={index * 0.06} key={benefit.id}>
            <IconFeature benefit={benefit} layout="column" divided={index > 0} />
          </Reveal>
        ))}
      </ul>
    </Section>
  );
}

/** 5 · Cámara Cuántica, con el domo brillante a la izquierda. */
export function BaChamber() {
  return (
    <section className={styles.chamber} aria-labelledby="ba-camara-title">
      <Container width="wide">
        <div className={styles.chamberGrid}>
          <Reveal className={styles.chamberMedia}>
            <AssetImage
              asset="buenos-aires/camara-domo"
              className={styles.chamberFrame}
              sizes="(min-width: 768px) 44vw, 100vw"
              alt={BA_CHAMBER.imageAlt}
            />
          </Reveal>

          <div className={styles.chamberCopy}>
            <Reveal>
              <h2 className={styles.chamberTitle} id="ba-camara-title">
                {BA_CHAMBER.title}
              </h2>
            </Reveal>
            <Reveal delay={0.06}>
              <GoldLine ornament="star" width="sm" />
            </Reveal>
            <Reveal delay={0.12}>
              <Prose align="center" size="sm" className={styles.chamberProse}>
                {BA_CHAMBER.paragraphs.map((p) => (
                  <p key={p}>{p}</p>
                ))}
              </Prose>
            </Reveal>
          </div>

          <Reveal className={styles.chamberOrnament} delay={0.16}>
            <span aria-hidden="true">
              <SacredGeometry variant="flower" size={140} />
            </span>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

/**
 * 6 · Bloque Azrahel: retrato circular, bio y escalera de luz.
 *
 * `spacing="tight"`: el mockup mete esta banda en 171 px. Nuestro cuerpo de
 * texto es legible (15,3 px) frente a los ~11 px del poster, asi que el alto no
 * puede bajar hasta ahi sin romper la accesibilidad — pero el relleno vertical
 * de la seccion si sobra (desviacion B-18).
 */
export function BaAzrahel() {
  return (
    <Section
      background="surface"
      labelledBy="ba-azrahel-title"
      width="wide"
      spacing="tight"
      className={styles.azrahel}
    >
      <div className={styles.azrahelGrid}>
        <Reveal className={styles.portrait}>
          <AssetImage
            asset="azrahel/circulo"
            className={styles.portraitFrame}
            sizes="(min-width: 768px) 20vw, 60vw"
            alt={BA_AZRAHEL.portraitAlt}
          />
        </Reveal>

        <div className={styles.azrahelCopy}>
          <Reveal>
            <h2 className={styles.azrahelName} id="ba-azrahel-title">
              {BA_AZRAHEL.name}
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <GoldLine ornament="diamond" width="sm" />
          </Reveal>
          <Reveal delay={0.12}>
            <Prose size="sm">
              {BA_AZRAHEL.paragraphs.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </Prose>
          </Reveal>
        </div>

        <Reveal className={styles.stairway} delay={0.16}>
          <AssetImage
            asset="buenos-aires/escalera-luz"
            className={styles.stairwayFrame}
            sizes="(min-width: 768px) 18vw, 60vw"
            alt={BA_AZRAHEL.stairwayAlt}
          />
        </Reveal>
      </div>
    </Section>
  );
}

/**
 * 7 · Entradas.
 *
 * `TicketCta` decide solo si pinta un enlace o un estado, leyendo `salesState` y
 * `ticketUrl` del dato (ADR-003 §3). Esta pagina no decide nada de eso, y por eso
 * no puede equivocarse.
 *
 * ── «UN POQUITO MÁS DE VUELO» (encargo del cliente, 28-jul-2026) ─────────────
 * Lo dijeron sobre esta seccion, y en condicional: «compra tus entradas esta
 * perfecto, hacerlo a lo mejor un poco mejor, un poquito mas de vuelo y tal».
 *
 * Tenia titulo, filete y boton, y nada mas: era la unica banda de la pagina que
 * no decia NADA. Se le anaden dos frases, y ninguna es un dato nuevo — la
 * primera son la ciudad y las fechas que ya estan en el hero, y la segunda dice
 * a donde lleva el boton. Esa segunda es la que de verdad quita friccion: en el
 * ultimo clic antes de pagar, saber que se sale a la tienda OFICIAL de la gira
 * —y no a un tercero cualquiera— es lo que se pregunta todo el que duda.
 *
 * SIN PRECIO Y SIN ESCASEZ: el precio vive en la ficha (orden del ORACLE) y
 * «quedan pocas» esta prohibido (ADR-003 §6). «Mas vuelo» no es mas presion.
 */
export function BaTickets({ event }: Props) {
  return (
    <section className={styles.tickets} aria-labelledby="ba-entradas-title">
      <span className={styles.flagStrip}>
        <AssetImage
          asset="buenos-aires/bandera-seda-velo"
          className={styles.flagStripFrame}
          sizes="(min-width: 768px) 28vw, 100vw"
          alt=""
        />
      </span>

      <Container width="default">
        <div className={styles.ticketsInner}>
          <Reveal>
            <h2 className={styles.ticketsTitle} id="ba-entradas-title">
              {BA_TICKETS.title}
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <GoldLine ornament="diamond" width="sm" />
          </Reveal>
          <Reveal delay={0.1}>
            <p className={styles.ticketsLead}>
              {formatDateRangeLong(event)}, en {event.city}. Nos vemos allí.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className={styles.ticketsCta}>
              <TicketCta event={event} variant="premium" size="lg" />
            </div>
          </Reveal>
          <Reveal delay={0.16}>
            <p className={styles.ticketsNota}>Compra en la tienda oficial de la gira.</p>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}

/**
 * "Volver a la gira" — lo pide `07_ARQUITECTURA_LANDINGS.md` §6.1 por su nombre.
 *
 * La pagina YA tenia cuatro caminos de vuelta al hub (la marca de la cabecera, el
 * item "La gira" de la nav, el mismo en el drawer, y el bloque de marca del hero
 * con su `aria-label`). Este es el quinto y el unico ROTULADO: quien entra por un
 * anuncio directo a la ciudad no tiene por que interpretar que un logo es un
 * enlace. Cuesta nada y cierra el requisito de forma literal.
 */
export function BaVolverALaGira() {
  return (
    <Section background="alt" spacing="tight" width="default">
      <div className={styles.volver}>
        <Link className={styles.volverLink} to="/">
          <span className={styles.volverFlecha} aria-hidden="true">
            <Sparkle size={14} variant="line" />
          </span>
          Volver a la gira
        </Link>
      </div>
    </Section>
  );
}

